<template>
  <div>
    <h3 class="text-center text-base text-gray-dark font-bold flex justify-center">
      {{ title }}

      <vs-tooltip v-if="hint" class="flex ml-1 items-center" position="bottom">
        <template #content>
          <div v-html="hint"/>
        </template>
        <vs-icon icon="info" class="text-gray cursor-default"/>
      </vs-tooltip>
    </h3>

    <with-loader class="flex flex-col justify-between py-6"
                 :class="{ 'h-64': isLoading, 'h-full': !isLoading }"
                 :id="id"
                 :loading="isLoading"
                 use-as-overlay
    >
      <div v-if="!hasData || isLoading" class="h-full flex justify-center items-center">
        <span v-if="!isLoading" class="font-bold text-gray text-6xl pb-16">–</span>
      </div>
      <vue-apex-charts v-else
                       :height="`${chartHeight}px`"
                       :series="data"
                       :options="options"
                       class="md:pt-6"
      />
    </with-loader>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import isEmpty from 'ramda/src/isEmpty'
import tailwind from '../../../../tailwind.config'
import WithLoader from '@/components/common/WithLoader'

export default {
  name: 'BarChart',

  components: { VueApexCharts, WithLoader },

  props: {
    id: {
      type: String,
      required: true
    },
    data: {
      type: Array,
      required: true
    },
    isLoading: {
      type: Boolean,
      required: true
    },
    isStacked: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    hint: {
      type: String,
      default: ''
    },
    categories: {
      type: Array,
      default: () => []
    },
    tooltipBuilder: {
      type: Object
    },
    total: {
      type: Number,
      default: 0
    },
    outcomeTypes: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      windowWidth: window.innerWidth
    }
  },

  computed: {
    hasData() {
      return !isEmpty(this.data)
    },

    chartHeight() {
      if (this.windowWidth < 768) return this.windowWidth * 0.5

      const chartHeight = this.windowWidth * 0.36
      return chartHeight < 250 ? chartHeight : 250
    },

    options() {
      return {
        colors: this.data.map(chartData => tailwind.theme.colors[chartData.colorType]),
        chart: {
          type: 'bar',
          stacked: this.isStacked,
          toolbar: {
            show: false
          },
          zoom: {
            enabled: false
          }
        },
        yaxis: {
          labels: {
            formatter: (value) => { return `${value.toFixed(0)}%` },
            style: {
              fontSize: tailwind.theme.fontSize.sm
            }
          }
        },
        xaxis: {
          categories: this.categories,
          axisTicks: {
            show: false
          },
          axisBorder: {
            show: false
          },
          labels: {
            rotate: 0,
            hideOverlappingLabels: false,
            rotateAlways: false,
            offsetY: 5,
            trim: true,
            style: {
              colors: tailwind.theme.colors['soft-black'],
              fontWeight: tailwind.theme.fontWeight.semibold,
              fontSize: tailwind.theme.fontSize.sm
            }
          }
        },
        dataLabels: {
          enabled: true,
          offsetY: -20,
          style: {
            colors: [tailwind.theme.colors['soft-black']],
            fontWeight: tailwind.theme.fontWeight.normal
          },
          formatter: (value, config) => {
            const { w, dataPointIndex } = config
            const series = w.config.series.map(({ data }) => data)
            return `${this.tooltipBuilder.getCategoryTotal(series, dataPointIndex).toFixed(2)}%`
          }
        },
        legend: {
          show: false
        },
        stroke: {
          width: 2,
          colors: [tailwind.theme.colors.white]
        },
        tooltip: {
          custom: this.tooltipBuilder ? (params) => this.tooltipBuilder.getTooltip(params, this, this.outcomeTypes) : undefined
        },
        plotOptions: {
          bar: {
            borderRadius: 10,
            columnWidth: '30%',
            dataLabels: {
              position: 'top'
            }
          }
        }
      }
    }
  },

  methods: {
    setWindowWidth() {
      this.windowWidth = window.innerWidth
    },

    // series are values by parts of columns e.g. [all fully automated values].'
    getCategoryTotal(series, columnIndex) {
      return series.map(seriesValues => seriesValues[columnIndex])
        .reduce((sum, seriesValue) => sum + seriesValue, 0)
    }
  },

  mounted() {
    window.addEventListener('resize', this.setWindowWidth)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.setWindowWidth)
  }
}
</script>

